<template>
  <div id="credits-management">
    <div class="flex">
      <div
        class="sticky left-0 z-30 flex items-center gap-2 px-6 py-3"
        :style="{ width: 'calc(100vw - 0.5rem)' }"
      >
        <BlInputSearch
          class="w-80"
          :input="searchInput"
          @changed="onInputSearch"
        />
        <div class="w-40">
          <BlInputFilter :input="filterInput" @changed="onChangeFilter" />
        </div>
        <BlSort
          class="w-60"
          v-model="sortInput"
          :options="sortOptions"
          :defaultValue="defaultSort"
          @change="onChangeSort($event)"
        />
      </div>
    </div>

    <div v-if="pageLoading" class="grid h-128 place-items-center">
      <div class="blum-loader"></div>
    </div>
    <div
      v-else-if="!creditsManagement.length"
      class="flex flex-col items-center justify-center h-128"
    >
      <BlTextBody1
        class="text-center"
        text="Credit management will appear once you add a new client.."
      />
    </div>
    <div v-else class="mb-8">
      <div
        id="credits-management-header"
        class="sticky z-20 flex items-center flex-grow rounded-t top-36 bg-slate-300"
      >
        <div class="sticky left-0 z-10 flex pl-6 bg-slate-100">
          <BlTooltip
            class="flex items-center flex-shrink-0 w-56 h-12 px-4 border-t border-l border-r rounded-tl bg-slate-300 border-slate-400"
          >
            <template v-slot:tooltip>
              <div class="flex flex-col gap-1 text-center w-44">
                <b>Name</b>
                <div>Client name</div>
              </div>
            </template>
            <BlTextSubtitle3 text="Name" class="truncate" />
          </BlTooltip>
        </div>
        <BlTooltip
          class="flex items-center flex-shrink-0 w-40 h-12 px-4 border-t bg-slate-300 border-slate-400"
        >
          <template v-slot:tooltip>
            <div class="flex flex-col gap-1 text-center w-44">
              <b>Client status</b>
              <div>The current status of the client.</div>
            </div>
          </template>
          <BlTextSubtitle3 text="Client status" class="truncate" />
        </BlTooltip>
        <BlTooltip
          class="flex items-center flex-shrink-0 w-40 h-12 px-4 border-t bg-slate-300 border-slate-400"
        >
          <template v-slot:tooltip>
            <div class="flex flex-col gap-1 text-center w-44">
              <b>Branch</b>
              <div>The branch of the client.</div>
            </div>
          </template>
          <BlTextSubtitle3 text="Branches" class="truncate" />
        </BlTooltip>
        <BlTooltip
          class="flex items-center flex-shrink-0 w-40 h-12 px-4 border-t bg-slate-300 border-slate-400"
        >
          <template v-slot:tooltip>
            <div class="flex flex-col gap-1 text-center w-44">
              <b>Available credits</b>
              <div>
                The total number of available credits that have not yet expired.
              </div>
            </div>
          </template>
          <BlTextSubtitle3 text="Available credits" class="truncate" />
        </BlTooltip>
        <BlTooltip
          class="flex items-center flex-shrink-0 w-40 h-12 px-4 border-t bg-slate-300 border-slate-400"
        >
          <template v-slot:tooltip>
            <div class="flex flex-col gap-1 text-center w-44">
              <b>Reserved credits</b>
              <div>
                The total number of credits reserved for appointments but not
                deducted until the appointments are concluded.
              </div>
            </div>
          </template>
          <BlTextSubtitle3 text="Reserved credits" class="truncate" />
        </BlTooltip>
        <BlTooltip
          class="flex items-center flex-shrink-0 w-40 h-12 px-4 border-t bg-slate-300 border-slate-400"
        >
          <template v-slot:tooltip>
            <div class="flex flex-col gap-1 text-center w-44">
              <b>Unused credits</b>
              <div>
                The total number of credits not yet reserved for any
                appointments.
              </div>
            </div>
          </template>
          <BlTextSubtitle3 text="Unused credits" class="truncate" />
        </BlTooltip>
        <BlTooltip
          class="flex items-center flex-shrink-0 w-40 h-12 px-4 border-t bg-slate-300 border-slate-400"
        >
          <template v-slot:tooltip>
            <div class="flex flex-col gap-1 text-center w-44">
              <b>Scheduled sessions</b>
              <div>
                The total number of sessions that have been scheduled from
                appointments.
              </div>
            </div>
          </template>
          <BlTextSubtitle3 text="Scheduled sessions" class="truncate" />
        </BlTooltip>
        <BlTooltip
          class="flex items-center flex-shrink-0 w-40 h-12 px-4 border-t bg-slate-300 border-slate-400"
        >
          <template v-slot:tooltip>
            <div class="flex flex-col gap-1 text-center w-44">
              <b>Expiration date</b>
              <div>
                The earliest expiration date of the available credits.
              </div>
            </div>
          </template>
          <BlTextSubtitle3 text="Expiration date" class="truncate" />
        </BlTooltip>
        <BlTooltip
          class="flex items-center flex-shrink-0 w-40 h-12 px-4 border-t bg-slate-300 border-slate-400"
        >
          <template v-slot:tooltip>
            <div class="flex flex-col gap-1 text-center w-44">
              <b>Reschedule credits</b>
              <div>
                The total number of available reschedule credits that have not
                yet expired.
              </div>
            </div>
          </template>
          <BlTextSubtitle3 text="Reschedule credits" class="truncate" />
        </BlTooltip>
        <BlTooltip
          class="flex items-center flex-shrink-0 w-40 h-12 px-4 border-t bg-slate-300 border-slate-400"
        >
          <template v-slot:tooltip>
            <div class="flex flex-col gap-1 text-center w-44">
              <b>Balance</b>
              <div>
                The total amount of available credits in the local currency.
              </div>
            </div>
          </template>
          <BlTextSubtitle3 text="Balance" />
        </BlTooltip>
        <div class="sticky right-0 z-10 flex flex-grow pr-6 bg-slate-100">
          <div
            class="flex items-center flex-grow flex-shrink-0 w-20 h-12 px-4 border-t border-r rounded-tr bg-slate-300 border-slate-400"
          >
            <BlTextSubtitle3 text="" />
          </div>
        </div>
      </div>

      <CreditItem
        v-for="(client, idx) in creditsManagement"
        :key="client.id"
        :client="client"
        :isLast="idx + 1 === creditsManagement.length"
      />
    </div>

    <div v-if="totalCreditsManagement > 25" class="flex mb-36">
      <BlInputPagination
        class="sticky left-6"
        :style="{ width: 'calc(100vw - 3rem - 0.5rem)' }"
        :page="page"
        :perPage="perPage"
        :total="totalCreditsManagement"
        @changed="changePage"
        @changed-per-page="changePerPage"
      />
    </div>
    <div v-else class="mb-46"></div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import debounce from "lodash/debounce";
import { displayDate, displayPrice } from "@/plugins/helper.js";

import CreditItem from "./creditsManagement/Item.vue";

import BlTextTitle3 from "../BlTextTitle3.vue";
import BlTextSubtitle3 from "../BlTextSubtitle3.vue";
import BlTextBody1 from "../BlTextBody1.vue";
import BlTextBody2 from "../BlTextBody2.vue";
import BlTooltip from "../BlTooltip.vue";
import BlChip from "../BlChip.vue";
import BlInputSearch from "../BlInputSearch.vue";
import BlInputFilter from "../BlInputFilter.vue";
import BlSort from "../BlSort.vue";
import BlInputButton from "../BlInputButton.vue";
import BlInputPagination from "../BlInputPagination.vue";

export default {
  name: "CreditsManagement",
  components: {
    CreditItem,
    //
    BlTextTitle3,
    BlTextSubtitle3,
    BlTextBody1,
    BlTextBody2,
    BlTooltip,
    BlChip,
    BlInputSearch,
    BlInputFilter,
    BlSort,
    BlInputButton,
    BlInputPagination
  },
  data() {
    const defaultSort = { primary: "name", secondary: "asc" };
    return {
      defaultSort,
      pageLoading: true,
      page: 1,
      perPage: 25,
      searchInput: {
        placeholder: "Search by name",
        model: ""
      },
      filterInput: {
        model: [[]],
        tabs: [
          {
            name: "Client Status",
            kind: "multi-tag",
            options: [
              {
                name: "Active",
                value: "active",
                color: "#789719",
                backgroundColor: "#F2F8CF"
              },
              {
                name: "Discharged",
                value: "archived",
                color: "#F63D68",
                backgroundColor: "#FFF1F3"
              },
              {
                name: "At risk of discharge",
                value: "at_risk_of_discharge",
                color: "#FB6514",
                backgroundColor: "#FFF6ED"
              }
            ]
          }
        ]
      },
      sortInput: defaultSort,
      sortOptions: {
        primary: [
          { value: "name", label: "Name" },
          { value: "available_credits", label: "Available credits" },
          { value: "unused_credits", label: "Unused credits" },
          { value: "expiration_date", label: "Expiration date" }
          // { value: "created_at", label: "Date created" },
        ],
        secondary: [
          { value: "asc", label: "Ascending" },
          { value: "desc", label: "Descending" }
        ]
      }
    };
  },
  computed: {
    ...mapGetters([
      "currentUser",
      "currentCenter",
      "creditsManagement",
      "totalCreditsManagement"
    ]),
    enableBranch() {
      return this.currentCenter.enable_branch;
    },
    getParams() {
      let params = `?page=${this.page}&per_page=${this.perPage}`;
      const query = this.searchInput.model;
      const statuses = this.filterInput.model[0];
      const branches = this.filterInput.model.at(1) || [];
      const sort = this.sortInput;

      if (query) params += `&name=${query}`;
      if (statuses.length > 0) {
        params += `&status=${statuses.map(a => a.value).join(",")}`;
      }
      if (branches.length > 0) {
        params += `&branch_ids=${branches.map(a => a.value).join(",")}`;
      }

      /**
       * - sort mapping -
       * name_asc -- name_desc
       * available_credits_asc -- available_credits_desc
       * unused_credits_asc -- unused_credits_desc
       * expiration_date_asc -- expiration_date_desc
       * created_at_asc -- created_at_desc
       */
      // prettier-ignore
      const sorts = {
        name: { asc: "name_asc", desc: "name_desc" },
        available_credits: { asc: "available_credits_asc", desc: "available_credits_desc" },
        unused_credits: { asc: "unused_credits_asc", desc: "unused_credits_desc" },
        expiration_date: { asc: "expiration_date_asc", desc: "expiration_date_desc" },
        created_at: { asc: "created_at_asc", desc: "created_at_desc" }
      };
      params += `&sort=${sorts[sort.primary][sort.secondary]}`;

      return params;
    }
  },
  watch: {},
  async mounted() {
    this.pageLoading = true;

    const searchParams = this.$route.query;
    if (searchParams["page"]) {
      this.page = Number(searchParams["page"]);
    }
    if (searchParams["per_page"]) {
      this.perPage = Number(searchParams["per_page"]);
    }
    if (searchParams["name"]) {
      this.searchInput.model = searchParams["name"];
    }
    const filter = [[]];
    if (searchParams["status"]) {
      const arr = searchParams["status"].split(",").map(a => {
        const found = this.filterInput.tabs[0].options.find(i => i.value === a);
        return found || { value: a };
      });
      filter[0] = arr;
    }

    if (searchParams["sort"]) {
      const temp = searchParams["sort"] || "";
      // prettier-ignore
      const sorts = {
        name_asc: { primary: "name", secondary: "asc" },
        name_desc: { primary: "name", secondary: "desc" },
        available_credits_asc: { primary: "available_credits", secondary: "asc" },
        available_credits_desc: { primary: "available_credits", secondary: "desc" },
        unused_credits_asc: { primary: "unused_credits", secondary: "asc" },
        unused_credits_desc: { primary: "unused_credits", secondary: "desc" },
        expiration_date_asc: { primary: "expiration_date", secondary: "asc" },
        expiration_date_desc: { primary: "expiration_date", secondary: "desc" }
      };
      if (temp && sorts[temp]) {
        this.sortInput = sorts[temp];
      }
    }

    if (this.enableBranch) {
      const tab = {
        name: "Branches",
        kind: "multi-tag",
        options: [],
        url: `/api/v1/branches?sort=name_asc&query=`,
        fetchPage: 1,
        dataKey: "branches",
        countKey: "total_count",
        optionKeys: {
          value: "id",
          label: "name",
          color: "color",
          backgroundColor: "background_color"
        }
      };
      this.filterInput.tabs = [...this.filterInput.tabs, tab];
    }
    if (searchParams["branch_ids"]) {
      const arr = searchParams["branch_ids"].split(",");
      filter[1] = arr.map(i => ({ value: Number(i) }));
    }
    this.filterInput.model = filter;

    this.fetchCreditsManagement();
  },
  methods: {
    ...mapActions(["getCreditsManagement"]),
    displayDate,
    displayPrice,
    priceFormats(price = 0) {
      const settings = this.currentCenter.currency_settings;
      return this.displayPrice({ settings, price });
    },
    async fetchCreditsManagement() {
      this.pageLoading = true;
      await this.getCreditsManagement({ params: this.getParams });
      this.pageLoading = false;

      // sync url path for page
      const incomingPath = `/finance${this.getParams}`;
      if (this.$route.fullPath !== incomingPath) {
        return this.$router.push(incomingPath);
      }
    },
    //
    onInputSearch(payload) {
      payload.input.model = payload.value;
      this.onSearching();
    },
    onSearching: debounce(function() {
      this.page = 1;
      this.fetchCreditsManagement();
    }, 1000),
    onChangeFilter(payload) {
      payload.input.model = payload.value;
      this.page = 1;
      this.fetchCreditsManagement();
    },
    onChangeSort(val) {
      const { primary, secondary } = this.sortInput;
      if (val.primary !== primary || val.secondary !== secondary) {
        this.sortInput = val;
        this.page = 1;
        this.fetchCreditsManagement();
      }
    },
    changePage(page) {
      if (page === this.page) return;
      this.page = page;
      this.fetchCreditsManagement();
    },
    changePerPage(perPage) {
      if (perPage === this.perPage) return;
      this.page = 1;
      this.perPage = perPage;
      this.fetchCreditsManagement();
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/finance/CreditsManagement.vue